import React, { useState, useEffect } from "react"
import { useFirebase } from "../context/FirebaseContext"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../firebase/firebase"
import { Save, AlertCircle } from "lucide-react"

const UserSettings = () => {
  const { currentUser, getUserData } = useFirebase()
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  
  // Form state
  const [displayName, setDisplayName] = useState("")
  const [learningGoal, setLearningGoal] = useState("10")
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        setLoading(true)
        try {
          const data = await getUserData(currentUser.uid)
          setUserData(data)
          
          // Set form values
          setDisplayName(data?.displayName || currentUser.displayName || "")
          setLearningGoal(data?.learningGoal?.split(" ")[0] || "10")
        } catch (error) {
          console.error("Error fetching user data:", error)
          setError("Failed to load your settings. Please try again.")
        } finally {
          setLoading(false)
        }
      }
    }
    
    fetchUserData()
  }, [currentUser, getUserData])
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess(false)
    setSaving(true)
    
    try {
      if (!currentUser) {
        throw new Error("You must be logged in to update settings")
      }
      
      // Update user document in Firestore
      const userRef = doc(db, "users", currentUser.uid)
      await updateDoc(userRef, {
        displayName,
        learningGoal: `${learningGoal} min/day`
      })
      
      // Also update localStorage to keep app settings in sync
      const currentSettings = JSON.parse(
        localStorage.getItem("hopLanguageAppSettings") || "{}"
      )
      const updatedSettings = {
        ...currentSettings,
        dailyGoal: learningGoal,
      }
      localStorage.setItem(
        "hopLanguageAppSettings",
        JSON.stringify(updatedSettings)
      )
      
      setSuccess(true)
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false)
      }, 3000)
    } catch (error) {
      console.error("Error updating settings:", error)
      setError(error.message)
    } finally {
      setSaving(false)
    }
  }
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3A6E]"></div>
      </div>
    )
  }
  
  if (!currentUser) {
    return (
      <div className="text-center py-6">
        <p className="text-[#A0A0A0]">You must be logged in to view your settings</p>
      </div>
    )
  }
  
  return (
    <div className="bg-[#1E1E1E] rounded-lg p-6">
      <h3 className="text-white font-semibold mb-4">User Settings</h3>
      
      {error && (
        <div className="bg-red-900/30 text-red-400 p-3 rounded-lg mb-4 flex items-start">
          <AlertCircle size={18} className="mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 text-green-400 p-3 rounded-lg mb-4">
          Settings saved successfully!
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[#A0A0A0] text-sm mb-1">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="bg-[#2C2C2C] text-white p-2 rounded-lg w-full"
            placeholder="Your name"
          />
        </div>
        
        <div>
          <label className="block text-[#A0A0A0] text-sm mb-1">Daily Learning Goal</label>
          <select
            value={learningGoal}
            onChange={(e) => setLearningGoal(e.target.value)}
            className="bg-[#2C2C2C] text-white rounded-lg px-3 py-2 w-full border-none outline-none"
          >
            <option value="5">5 minutes</option>
            <option value="10">10 minutes</option>
            <option value="15">15 minutes</option>
            <option value="20">20 minutes</option>
            <option value="30">30 minutes</option>
          </select>
        </div>
        
        <button
          type="submit"
          disabled={saving}
          className="bg-[#FF3A6E] text-white py-2 px-6 rounded-lg flex items-center hover:bg-[#ff2a5e] disabled:opacity-50 disabled:hover:bg-[#FF3A6E]"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={18} className="mr-2" />
              Save Settings
            </>
          )}
        </button>
      </form>
    </div>
  )
}

export default UserSettings